import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Loader2, Trophy, BarChart3, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useIFCarteiraResumo } from '../hooks/useIFCarteira';
import { fetchGlobalRanking } from '../services/ifRankingService';
import { riskBadge, trendIcon, scoreColor } from '../components/IFRiskBadges';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { DataTable, type Column } from '@/components/system/DataTable';
import { FilterBar } from '@/components/system/FilterBar';
import type { IFContratoResumo } from '../types/financiamento.types';

type RankedContract = IFContratoResumo & { globalPosition: number | null; globalTotal: number };

const IFRankingPage: React.FC = () => {
  const navigate = useNavigate();
  const { resumos, isLoading: loadIF } = useIFCarteiraResumo();
  const [search, setSearch] = useState('');

  const { data: globalRanking = [], isLoading: loadGlobal } = useQuery({
    queryKey: ['if-global-ranking'],
    queryFn: () => fetchGlobalRanking(),
  });

  const isLoading = loadIF || loadGlobal;

  const globalAvg = globalRanking.length > 0
    ? {
        score: globalRanking.reduce((s, r) => s + (r.score_risco ?? 0), 0) / globalRanking.length,
        prob: globalRanking.reduce((s, r) => s + (r.prob_atraso_mc ?? 0), 0) / globalRanking.length,
        total: globalRanking.length,
      }
    : { score: 0, prob: 0, total: 0 };

  const ifAvg = resumos.length > 0
    ? {
        score: resumos.reduce((s, r) => s + (r.score_risco ?? 0), 0) / resumos.length,
        prob: resumos.reduce((s, r) => s + (r.prob_atraso ?? 0), 0) / resumos.length,
        total: resumos.length,
      }
    : { score: 0, prob: 0, total: 0 };

  const rankedContracts: RankedContract[] = useMemo(() => {
    let list = resumos.map(r => {
      const globalPosition = globalRanking.findIndex(g => g.contract_id === r.contrato_id) + 1;
      return {
        ...r,
        globalPosition: globalPosition > 0 ? globalPosition : null,
        globalTotal: globalRanking.length,
      };
    }).sort((a, b) => (a.score_risco ?? 0) - (b.score_risco ?? 0));

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.contrato_nome.toLowerCase().includes(q) || r.contratada.toLowerCase().includes(q));
    }
    return list;
  }, [resumos, globalRanking, search]);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const scoreDelta = ifAvg.score - globalAvg.score;
  const probDelta = (ifAvg.prob - globalAvg.prob) * 100;

  const columns: Column<RankedContract>[] = [
    { key: 'contrato', label: 'Contrato', render: r => <span className="font-medium max-w-[200px] truncate block">{r.contrato_nome}</span> },
    { key: 'contratada', label: 'Contratada', render: r => <span className="text-sm text-muted-foreground max-w-[150px] truncate block">{r.contratada}</span> },
    { key: 'score', label: 'Score', align: 'center', render: r => <span className={`font-medium ${scoreColor(r.score_risco)}`}>{(r.score_risco ?? 0).toFixed(1)}</span> },
    { key: 'classificacao', label: 'Classificação', align: 'center', render: r => riskBadge(r.classificacao_risco) },
    { key: 'tendencia', label: 'Tendência', align: 'center', render: r => trendIcon(r.tendencia) },
    { key: 'prob', label: 'Prob. Atraso', align: 'center', render: r => <span className="text-sm">{((r.prob_atraso ?? 0) * 100).toFixed(0)}%</span> },  
    { key: 'progresso', label: 'Progresso', align: 'center', render: r => (
      <div className="flex items-center gap-2 justify-center">
        <Progress value={Math.min(100, r.progresso_fisico_pct)} className="h-1.5 w-16" />
        <span className="text-xs font-medium">{r.progresso_fisico_pct.toFixed(1)}%</span>
      </div>
    )},
    { key: 'pos', label: 'Posição Global', align: 'center', render: r => r.globalPosition ? (
      <Badge variant="outline" className="text-xs">{r.globalPosition}º / {r.globalTotal}</Badge>
    ) : <span>—</span> },
  ];

  return (
    <div className="space-y-6">
        <PageHeader
          title="Ranking de Contratos"
          subtitle="Posicionamento dos contratos monitorados em relação à plataforma"
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Trophy className="h-4 w-4" />Score Médio — Carteira</div>
            <p className={`text-2xl font-bold mt-1 ${scoreColor(ifAvg.score)}`}>{ifAvg.score.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground">{ifAvg.total} contrato(s) monitorado(s)</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><BarChart3 className="h-4 w-4" />Score Médio — Plataforma</div>
            <p className={`text-2xl font-bold mt-1 ${scoreColor(globalAvg.score)}`}>{globalAvg.score.toFixed(1)}</p>
            <p className="text-xs text-muted-foreground">{globalAvg.total} contrato(s) na plataforma</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              {scoreDelta > 0 ? <ArrowUpRight className="h-4 w-4 text-destructive" /> : <ArrowDownRight className="h-4 w-4" style={{ color: 'hsl(var(--success))' }} />}
              Delta Score
            </div>
            <p className={`text-2xl font-bold mt-1 ${scoreDelta > 0 ? 'text-destructive' : 'text-[hsl(var(--success))]'}`}>
              {scoreDelta >= 0 ? '+' : ''}{scoreDelta.toFixed(1)}
            </p>
            <p className="text-xs text-muted-foreground">{scoreDelta > 0 ? 'Acima da média (pior)' : 'Abaixo da média (melhor)'}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              {probDelta > 0 ? <ArrowUpRight className="h-4 w-4 text-destructive" /> : <ArrowDownRight className="h-4 w-4" style={{ color: 'hsl(var(--success))' }} />}
              Delta Prob. Atraso
            </div>
            <p className={`text-2xl font-bold mt-1 ${probDelta > 0 ? 'text-destructive' : 'text-[hsl(var(--success))]'}`}>
              {probDelta >= 0 ? '+' : ''}{probDelta.toFixed(1)}%
            </p>
            <p className="text-xs text-muted-foreground">vs. média da plataforma</p>
          </CardContent></Card>
        </div>

        <SectionCard title="Contratos Monitorados — Ranking" icon={Trophy}>
          <p className="text-xs text-muted-foreground mb-3">Ordenado por score de risco (melhor para pior). Posição global entre todos os contratos da plataforma.</p>
          <FilterBar className="mb-4">
            <Input
              placeholder="Buscar contrato ou contratada..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-xs h-8 text-sm"
            />
          </FilterBar>
          <DataTable
            columns={columns}
            data={rankedContracts}
            keyExtractor={r => r.contrato_id}
            emptyText="Nenhum contrato monitorado nesta carteira."
            onRowClick={r => navigate(`/financiamento/contrato/${r.contrato_id}`)}
          />
        </SectionCard>
    </div>
  );
};

export default IFRankingPage;
